import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { translateElement } from "../TraslateService";
import { isSpeechSupported, speak } from "../SpeechService";
import { applyListFunctions, resolveValue } from "../Utils/DynamicValueResolver";
import { animationCSS, bgStyleObj, buildGradientString, resolveDataValue, shadowCSS } from "../Utils/CssHelper";
import { Clock } from "../Utils/UIHelper";
import GoogleMapWidget from "../GoogleMapWidget";
import { buildYouTubeSrc } from "../Utils/leafHelpers";
import { QRCodeSVG } from "qrcode.react";
import { GroupRender } from "./PreviewGroupRender";
import { Model3DViewer } from "../Utils/Model3DViewer";
import { CatalogLeaf } from "../Widgets/CatalogLeaf";
import { CATALOG_TYPES } from "../Widgets/catalog";
import { KioskIcon } from "../Widgets/KioskIcon";
import { KeyboardElement, useKioskKeyboard } from "../Widgets/OnScreenKeyboard";

export function LeafRender({ node, dataCtx, vars, onVarChange, onAction, screenNodes, screenRef, globalDataStore, visibilityOverrides, itemIdx = 0 }) {
  // Hooks must be called at top level (not conditionally)
  const keyboard = useKioskKeyboard();
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [resolvedCenter, setResolvedCenter] = useState(null);
  const [animatedText, setAnimatedText] = useState(null);
  const animFrameRef = useRef(null);
  const scrambleIntervalRef = useRef(null);

  // Confetti particles — computed unconditionally to satisfy Rules of Hooks
  const confettiParticles = useMemo(() => {
    const count = node.confettiCount || 60;
    const speed = node.confettiSpeed || 3;
    const size = node.confettiSize || 8;
    const colorList = (node.confettiColors || "#ff4757,#ffa502,#2ed573,#1e90ff,#eccc68,#ff6b81,#a29bfe,#ff9ff3")
      .split(",").map(c => c.trim()).filter(Boolean);
    return Array.from({ length: count }, (_, i) => {
      const r  = (i * 7919 + 31) % 1000 / 1000;
      const r2 = (i * 3571 + 17) % 1000 / 1000;
      const r3 = (i * 6271 + 53) % 1000 / 1000;
      const r4 = (i * 4133 +  7) % 1000 / 1000;
      return {
        id: i,
        x: r * 100,
        delay: r2 * speed,
        duration: speed * 0.7 + r3 * speed * 0.6,
        color: colorList[i % colorList.length],
        size: size * (0.5 + r4 * 0.8),
        isCircle: i % 3 !== 1,
        bx: (r  - 0.5) * 300,
        by: -(50 + r2 * 150),
        br: r3 * 720 - 360,
      };
    });
  }, [node.confettiCount, node.confettiSpeed, node.confettiSize, node.confettiColors]);

  // countUp / scramble animation for text nodes
  useEffect(() => {
    if (node.type !== "text") { setAnimatedText(null); return; }
    const anim = node.styles?.animation;
    if (anim !== "countUp" && anim !== "scramble") { setAnimatedText(null); return; }
    const rawVal = node.dynamic && node.dynamicKey ? (vars?.[node.dynamicKey] ?? node.staticText ?? "") : (node.staticText ?? "");
    const target = String(rawVal);
    const delay = (node.styles?.animationDelay || 0) * 1000;
    const duration = (node.styles?.animationDuration || 1.5) * 1000;

    if (anim === "countUp") {
      const numMatch = target.match(/^([^0-9-]*)(-?[\d.]+)([^0-9]*)$/);
      const prefix = numMatch ? numMatch[1] : "";
      const num = numMatch ? parseFloat(numMatch[2]) : NaN;
      const suffix = numMatch ? numMatch[3] : "";
      if (isNaN(num)) { setAnimatedText(null); return; }
      const isInt = Number.isInteger(num);
      const startMs = Date.now() + delay;
      setAnimatedText(prefix + "0" + suffix);
      const tick = () => {
        const now = Date.now();
        if (now < startMs) { animFrameRef.current = requestAnimationFrame(tick); return; }
        const progress = Math.min((now - startMs) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const cur = eased * num;
        setAnimatedText(prefix + (isInt ? Math.round(cur) : Math.round(cur * 10) / 10) + suffix);
        if (progress < 1) animFrameRef.current = requestAnimationFrame(tick);
      };
      animFrameRef.current = requestAnimationFrame(tick);
    } else {
      const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#%&";
      const startMs = Date.now() + delay;
      setAnimatedText(target.split("").map(c => c === " " ? " " : CHARS[Math.floor(Math.random() * CHARS.length)]).join(""));
      scrambleIntervalRef.current = setInterval(() => {
        const now = Date.now();
        if (now < startMs) return;
        const progress = Math.min((now - startMs) / duration, 1);
        const revealed = Math.floor(progress * target.length);
        setAnimatedText(
          target.split("").map((c, i) => {
            if (i < revealed || c === " ") return c;
            return CHARS[Math.floor(Math.random() * CHARS.length)];
          }).join("")
        );
        if (progress >= 1) { clearInterval(scrambleIntervalRef.current); setAnimatedText(target); }
      }, 50);
    }
    return () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (scrambleIntervalRef.current) clearInterval(scrambleIntervalRef.current);
    };
  }, [node.id, node.type, node.styles?.animation, node.staticText, node.dynamic, node.dynamicKey, vars, node.styles?.animationDelay, node.styles?.animationDuration]);

  // Geocode center search string for googleMap nodes when lat/lng not stored
  useEffect(() => {
    if (node.type !== "googleMap") return;
    if (node.googleMapCenterLat && node.googleMapCenterLng) {
      setResolvedCenter({ lat: parseFloat(node.googleMapCenterLat), lng: parseFloat(node.googleMapCenterLng), title: node.googleMapCenterTitle || "Center" });
      return;
    }
    if (!node.googleMapApiKey || !node.googleMapCenterSearch) return;
    (async () => {
      try {
        await new Promise((resolve, reject) => {
          if (window.google?.maps) { resolve(); return; }
          const ex = document.querySelector('script[src*="maps.googleapis.com"]');
          if (ex) { ex.addEventListener("load", resolve); ex.addEventListener("error", reject); return; }
          const s = document.createElement("script");
          s.src = `https://maps.googleapis.com/maps/api/js?key=${node.googleMapApiKey}&libraries=places,geometry`;
          s.async = true; s.onload = resolve; s.onerror = reject;
          document.head.appendChild(s);
        });
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: node.googleMapCenterSearch }, (results, status) => {
          if (status === "OK" && results[0]) {
            const loc = results[0].geometry.location;
            setResolvedCenter({ lat: loc.lat(), lng: loc.lng(), title: results[0].formatted_address });
          }
        });
      } catch (e) { console.warn("Preview center geocoding failed:", e); }
    })();

  }, [node.type, node.googleMapApiKey, node.googleMapCenterLat, node.googleMapCenterLng, node.googleMapCenterSearch]);
  const handleTranslate = useCallback(async (node) => {
  try {
    const targetLang = node.translateTargetLanguage || "es";
    const sourceLang = node.translateSourceLanguage || "auto";

    if (node.translateMode === "screen") {
      const screenElement = screenRef?.current || null;
      if (screenElement) {
        await translateElement(screenElement, targetLang, sourceLang);
      } else {
        console.warn("[Translate] screenRef is null — cannot translate screen");
      }
    }
  } catch (error) {
    console.warn("[Translate] Translation failed:", error);
  }
}, [screenRef]);

const handleSpeak = useCallback(async (node) => {
  try {
    if (!isSpeechSupported()) {
      // showToast("Speech synthesis not supported in your browser", true);
      return;
    }

    const screenElement = screenRef?.current || null;
    let textToSpeak = "";

    if (node.speakerMode === "screen" && screenElement) {
      textToSpeak = screenElement.innerText || screenElement.textContent || "";
    } else {
      textToSpeak = node.speakerCustomText || node.staticText || "No text to speak";
    }

    if (!textToSpeak.trim()) {
      // showToast("No text to speak", true);
      return;
    }

    await speak(textToSpeak, {
      voiceName: node.speakerVoice && node.speakerVoice !== "default" ? node.speakerVoice : undefined,
      rate: node.speakerRate || 1.0,
      pitch: node.speakerPitch || 1.0,
      volume: node.speakerVolume || 1.0,
    });
  } catch (error) {
    // showToast(`✕ Speech failed: ${error.message}`, true);
  }
}, [screenRef]);
  const nodeActions = Array.isArray(node.onClick) ? node.onClick : [];
  const hasNodeActions = nodeActions.length > 0;
  const runNodeActions = useCallback(async () => {
    if (onAction && hasNodeActions) {
      await onAction(nodeActions);
    }
  }, [onAction, hasNodeActions, nodeActions]);
  const onEndActions = Array.isArray(node.onEnd) ? node.onEnd : [];
  const hasEndActions = onEndActions.length > 0;
  const runOnEnd = useCallback(async () => {
    if (onAction && hasEndActions) { await onAction(onEndActions); }
  }, [onAction, hasEndActions, onEndActions]);
  // YouTube postMessage listener for onEnd (only active when node is youtube with onEnd actions)
  useEffect(() => {
    if (node.type !== "youtube" || !hasEndActions) return;
    const handler = (e) => {
      try {
        const data = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
        const isEnded =
          (data.event === "onStateChange" && data.info === 0) ||
          (data.event === "infoDelivery" && data.info?.playerState === 0);
        if (isEnded) runOnEnd();
      } catch (_) {}
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [node.type, hasEndActions, runOnEnd]);
  const [checkboxChecked, setCheckboxChecked] = useState(node.checkboxChecked || false);
  const [quizPage, setQuizPage] = useState(0);
  const htmlIframeRef = useRef(null);
  const [quizTimeLeft, setQuizTimeLeft] = useState(null);
  const quizTimerRef = useRef(null);
  const varsRef = useRef(vars);
  varsRef.current = vars;
  const onVarChangeRef = useRef(onVarChange);
  onVarChangeRef.current = onVarChange;

  // Per-page timer: resets each time the page changes
  useEffect(() => {
    if (node.type !== "quiz") return;
    if ((node.quizTimerMode || "per-page") !== "per-page") { setQuizTimeLeft(null); clearInterval(quizTimerRef.current); return; }
    const globalLimit = node.quizTimeLimit || 0;
    const currentPage = typeof varsRef.current?.[`${node.id}_page`] === "number" ? varsRef.current[`${node.id}_page`] : 0;
    const allQ = node.quizQuestions || [];
    const filtered = node.quizCategory ? allQ.filter(q => !q.category || q.category === node.quizCategory) : allQ;
    const perPage = node.quizQuestionsPerPage || 1;
    const pageStart = currentPage * perPage;
    const firstQ = filtered[pageStart];
    const effectiveLimit = firstQ?.timeLimit > 0 ? firstQ.timeLimit : globalLimit;
    if (!effectiveLimit) { setQuizTimeLeft(null); clearInterval(quizTimerRef.current); return; }
    setQuizTimeLeft(effectiveLimit);
    clearInterval(quizTimerRef.current);
    quizTimerRef.current = setInterval(() => {
      setQuizTimeLeft(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(quizTimerRef.current);
          const maxPage = Math.max(0, Math.ceil(filtered.length / perPage) - 1);
          if (currentPage < maxPage) onVarChangeRef.current?.(`${node.id}_page`, currentPage + 1);
          else onVarChangeRef.current?.(`${node.id}_submitted`, true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(quizTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.type, node.id, node.quizTimerMode, node.quizTimeLimit, node.quizCategory, node.quizQuestionsPerPage, vars?.[`${node.id}_page`]]);

  // Total quiz timer: starts once, counts down the full duration regardless of page changes
  useEffect(() => {
    if (node.type !== "quiz") return;
    if ((node.quizTimerMode || "per-page") !== "total") { return; }
    const globalLimit = node.quizTimeLimit || 0;
    if (!globalLimit) { setQuizTimeLeft(null); clearInterval(quizTimerRef.current); return; }
    setQuizTimeLeft(globalLimit);
    clearInterval(quizTimerRef.current);
    quizTimerRef.current = setInterval(() => {
      setQuizTimeLeft(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(quizTimerRef.current);
          onVarChangeRef.current?.(`${node.id}_submitted`, true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(quizTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.type, node.id, node.quizTimerMode, node.quizTimeLimit]);

  // HTML iframe — push updated vars into the iframe whenever they change
  useEffect(() => {
    if (node.type !== "html" || !node.htmlVarBridge) return;
    try { htmlIframeRef.current?.contentWindow?.postMessage({ type: "elm_vars", vars: vars || {} }, "*"); } catch (_) {}
  });

  // HTML iframe — listen for setKioskVar messages from iframe
  useEffect(() => {
    if (node.type !== "html" || !node.htmlVarBridge) return;
    const handler = e => {
      if (e.data?.type === "elm_var_set" && e.data.name != null) {
        onVarChangeRef.current?.(e.data.name, e.data.value);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.type, node.id, node.htmlVarBridge]);

  const s = node.styles;
  const txt = resolveValue(node, dataCtx, vars);
  const shadow = shadowCSS(s);
  const flt = s.blur ? `blur(${s.blur}px)` : undefined;
  const anim = animationCSS(s);
  const base = {
    position: "absolute", left: s.x, top: s.y, width: s.width, height: s.height,
    opacity: (s.opacity ?? 100) / 100, zIndex: s.zIndex || 1,
    boxSizing: "border-box", boxShadow: shadow, filter: flt,
    animation: anim, borderRadius: s.borderRadius || 0, overflow: "hidden",
  };

  // Visibility: hidden by default if node.visible===false, unless overridden at runtime
  const isVisible = node.visible !== false || visibilityOverrides?.[node.id] === true;
  if (!isVisible) return null;

  if (node.type === "text" || node.type === "clock") {
    const justifyMap = { left: "flex-start", center: "center", right: "flex-end" };
    const baseText = node.type === "clock" ? null : (txt || node.staticText || "");
    const rawText = animatedText !== null && node.type === "text" ? animatedText : baseText;
    const overflow = s.textOverflow || "clip";
    const autoFs = s.fontAutoSize && rawText ? Math.min(s.fontSize || 16, Math.max(8, Math.floor((s.width - 12) / (rawText.length || 1) * 1.6))) : (s.fontSize || 16);
    const isSpecialAnim = s.animation === "countUp" || s.animation === "scramble";
    const spanStyle = { fontWeight: s.fontWeight || "400", fontFamily: s.fontFamily || "Inter", fontSize: autoFs };
    if (overflow === "ellipsis") { spanStyle.overflow = "hidden"; spanStyle.textOverflow = "ellipsis"; spanStyle.whiteSpace = "nowrap"; spanStyle.display = "block"; spanStyle.maxWidth = "100%"; }
    else if (overflow === "wrap") { spanStyle.whiteSpace = "pre-wrap"; spanStyle.wordBreak = "break-word"; spanStyle.display = "block"; spanStyle.maxWidth = "100%"; }
    else { spanStyle.whiteSpace = "nowrap"; }
    const textStyle = { ...base, ...bgStyleObj(s, dataCtx), display: "flex", alignItems: "center", justifyContent: justifyMap[s.textAlign] || "flex-start", padding: "0 6px", overflow: "hidden",
      animation: isSpecialAnim ? undefined : (animationCSS(s) ? base.animation : undefined) };
    if (s.textGradientEnabled && s.textGradientStops && s.textGradientStops.length >= 2) {
      spanStyle.background = buildGradientString(s.textGradientStops, s.textGradientAngle);
      spanStyle.WebkitBackgroundClip = "text";
      spanStyle.WebkitTextFillColor = "transparent";
      spanStyle.backgroundClip = "text";
    } else {
      spanStyle.color = s.color || "#fff";
    }
    return <div style={{ ...textStyle, cursor: hasNodeActions ? "pointer" : textStyle.cursor }} onClick={hasNodeActions ? runNodeActions : undefined}>
      <span style={spanStyle}>{node.type === "clock" ? <Clock /> : rawText}</span>
    </div>;
  }

  if (node.type === "ticker") {
    const rawTicker = txt || node.staticText || "";
    const tickerOverflow = s.textOverflow || "clip";
    const tickerFs = s.fontAutoSize && rawTicker ? Math.min(s.fontSize || 12, Math.max(8, Math.floor((s.width - 12) / (rawTicker.length || 1) * 1.6))) : (s.fontSize || 12);
    const tickerStyle = { fontSize: tickerFs, fontFamily: s.fontFamily || "Inter", animation: "tickerScroll 15s linear infinite", paddingLeft: "100%" };
    if (tickerOverflow === "ellipsis") { tickerStyle.overflow = "hidden"; tickerStyle.textOverflow = "ellipsis"; tickerStyle.whiteSpace = "nowrap"; }
    else if (tickerOverflow === "wrap") { tickerStyle.whiteSpace = "pre-wrap"; tickerStyle.wordBreak = "break-word"; }
    else { tickerStyle.whiteSpace = "nowrap"; }
    if (s.textGradientEnabled && s.textGradientStops && s.textGradientStops.length >= 2) {
      tickerStyle.background = buildGradientString(s.textGradientStops, s.textGradientAngle);
      tickerStyle.WebkitBackgroundClip = "text";
      tickerStyle.WebkitTextFillColor = "transparent";
      tickerStyle.backgroundClip = "text";
    } else {
      tickerStyle.color = s.color || "#f0f4f5";
    }
    return <div style={{ ...base, ...bgStyleObj(s, dataCtx), display: "flex", alignItems: "center", cursor: hasNodeActions ? "pointer" : "default" }} onClick={hasNodeActions ? runNodeActions : undefined}>
      <div style={tickerStyle}>
        {rawTicker}
      </div>
    </div>;
  }

  if (node.type === "button") {
    const btnLabel = txt || node.staticText || "Button";
    const btnOverflow = s.textOverflow || "clip";
    const btnAutoFs = s.fontAutoSize && btnLabel ? Math.min(s.fontSize || 14, Math.max(8, Math.floor((s.width - 12) / (btnLabel.length || 1) * 1.6))) : (s.fontSize || 14);
    const buttonTextStyle = { fontSize: btnAutoFs, fontWeight: s.fontWeight || "600", fontFamily: s.fontFamily || "Inter" };
    if (btnOverflow === "ellipsis") { buttonTextStyle.overflow = "hidden"; buttonTextStyle.textOverflow = "ellipsis"; buttonTextStyle.whiteSpace = "nowrap"; buttonTextStyle.display = "block"; buttonTextStyle.maxWidth = "100%"; }
    else if (btnOverflow === "wrap") { buttonTextStyle.whiteSpace = "pre-wrap"; buttonTextStyle.wordBreak = "break-word"; buttonTextStyle.display = "block"; buttonTextStyle.maxWidth = "100%"; }
    if (s.textGradientEnabled && s.textGradientStops && s.textGradientStops.length >= 2) {
      buttonTextStyle.background = buildGradientString(s.textGradientStops, s.textGradientAngle);
      buttonTextStyle.WebkitBackgroundClip = "text";
      buttonTextStyle.WebkitTextFillColor = "transparent";
      buttonTextStyle.backgroundClip = "text";
    } else {
      buttonTextStyle.color = s.color || "#fff";
    }
    const hasIcon = s.iconEnabled && s.icon;
    const iconEl = hasIcon ? <KioskIcon name={s.icon} size={s.fontSize || 14} color={s.color || "#fff"} /> : null;
    const gap = s.iconGap || 6;
    const handleButtonClick = async () => {
      // Run special function first
      if (node.specialFn === "translate") {
        await handleTranslate(node);
      } else if (node.specialFn === "speak") {
        await handleSpeak(node);
      } else if (["quizNext","quizPrev","quizSubmit"].includes(node.specialFn)) {
        const quizNode = node.quizTargetId
          ? screenNodes?.find(n => n.id === node.quizTargetId)
          : screenNodes?.find(n => n.type === "quiz");
        if (quizNode) {
          const allQ = quizNode.quizQuestions || [];
          const filtered = quizNode.quizCategory ? allQ.filter(q => !q.category || q.category === quizNode.quizCategory) : allQ;
          const perPage = quizNode.quizQuestionsPerPage || 1;
          const curPage = typeof vars?.[`${quizNode.id}_page`] === "number" ? vars[`${quizNode.id}_page`] : 0;
          if (node.specialFn === "quizNext") {
            const maxPage = Math.max(0, Math.ceil(filtered.length / perPage) - 1);
            if (curPage < maxPage) onVarChange?.(`${quizNode.id}_page`, curPage + 1);
          } else if (node.specialFn === "quizPrev") {
            if (curPage > 0) onVarChange?.(`${quizNode.id}_page`, curPage - 1);
          } else if (node.specialFn === "quizSubmit") {
            const rawAnswers = vars?.[`${quizNode.id}_answers`];
            const answers = rawAnswers && typeof rawAnswers === "object" ? rawAnswers : {};
            let score = 0;
            filtered.forEach((q, i) => {
              const aType = q.answerType || "mcq";
              if (aType === "input") {
                const userAns = typeof answers[i] === "string" ? answers[i].toLowerCase().trim() : "";
                const correctAns = (q.correctText || "").toLowerCase().trim();
                if (userAns && correctAns && userAns === correctAns) score++;
              } else {
                if (answers[i] === q.correct) score++;
              }
            });
            const scoreVar = quizNode.quizScoreVar || `${quizNode.id}QuizScore`;
            const scorePct = filtered.length > 0 ? Math.round((score / filtered.length) * 100) + "%" : "0%";
            onVarChange?.(scoreVar, scorePct);
            onVarChange?.(`${quizNode.id}_submitted`, true);
          }
        }
      }
      // Then run onClick actions
      if (onAction) await onAction(node.onClick || []);
    };
    return <div
      onClick={handleButtonClick}
      style={{ ...base, ...bgStyleObj(s, dataCtx), display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", userSelect: "none", WebkitTapHighlightColor: "transparent", transition: "filter 0.15s, transform 0.1s", gap: hasIcon ? gap : 0 }}
      onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.15)"}
      onMouseLeave={e => e.currentTarget.style.filter = ""}
      onMouseDown={e => e.currentTarget.style.transform = "scale(0.97)"}
      onMouseUp={e => e.currentTarget.style.transform = ""}
    >
      {hasIcon && s.iconPosition !== "right" && iconEl}
      <span style={buttonTextStyle}>{btnLabel}</span>
      {hasIcon && s.iconPosition === "right" && iconEl}
    </div>;
  }
  if (node.type === "image") {
    const src = (node.dynamic && txt) || node.staticText || "";
    return <div style={{ ...base, cursor: hasNodeActions ? "pointer" : "default" }} onClick={hasNodeActions ? runNodeActions : undefined}>
      {src
        ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: s.objectFit || "cover", display: "block" }} />
        : null}
    </div>;
  }

  if (node.type === "input") {
    const varVal = (node.inputVar && vars) ? (vars[node.inputVar] || "") : "";
    const osk = node.inputVar && keyboard ? keyboard : null;
    return <div data-kiosk-input={node.id} style={{ ...base, ...bgStyleObj(s, dataCtx), border: `${s.borderWidth || 1}px solid ${s.borderColor || "#36454F"}` }} onClick={hasNodeActions ? runNodeActions : undefined}>
      <input value={varVal} placeholder={node.inputPlaceholder || node.inputVar || "Enter text..."}
        inputMode={osk?.hasKeyboard ? "none" : undefined} onFocus={osk ? () => osk.focus(node) : undefined} onPointerDown={osk ? () => osk.focus(node) : undefined}
        onChange={e => node.inputVar && onVarChange && onVarChange(node.inputVar, e.target.value)}
        style={{ width: "100%", height: "100%", background: "transparent", border: "none", outline: "none", color: s.color || "#f0f4f5", fontSize: s.fontSize || 14, fontFamily: s.fontFamily || "Inter", padding: "0 10px", boxSizing: "border-box" }} />
    </div>;
  }

  if (node.type === "qr") {
    const qrValue = (node.dynamic && txt) || node.staticText || "";
    const qrBg = s.backgroundColor || "#ffffff";
    const qrSize = Math.max(20, Math.min(s.width, s.height) - 12);
    return <div style={{ ...base, backgroundColor: qrBg, display: "flex", alignItems: "center", justifyContent: "center", cursor: hasNodeActions ? "pointer" : "default" }} onClick={hasNodeActions ? runNodeActions : undefined}>
      {qrValue
        ? <QRCodeSVG value={qrValue} size={qrSize} bgColor={qrBg} fgColor="#000000" level="M" />
        : null}
    </div>;
  }

if(node.type==="googleMap"){
  const sourceId=node.googleMapSourceId||"";
  const markersDataKey=node.googleMapMarkersDataKey||"";
  const latCol=node.googleMapLatColumn||"lat";
  const lngCol=node.googleMapLngColumn||"lng";
  const rawTitleCol=node.googleMapTitleColumn||"title";
  const titleCol=rawTitleCol.replace(/\$(\w+)/g,(_,varName)=>vars?.[varName]??varName);
  let sourceRows=(sourceId&&globalDataStore&&Array.isArray(globalDataStore[sourceId]))
    ?globalDataStore[sourceId]
    :node.googleMapMarkers||[];

  if(markersDataKey&&dataCtx!==null){
    const resolvedMarkers=resolveDataValue(markersDataKey,dataCtx);
    if(Array.isArray(resolvedMarkers)) sourceRows=resolvedMarkers;
  }

  const useLegacyExcelData = (!sourceId || !Array.isArray(globalDataStore?.[sourceId])) && node.googleMapExcelData && Array.isArray(node.googleMapExcelData);
  if(useLegacyExcelData){
    sourceRows=node.googleMapExcelData;

    // Backward-compatible category filter.
    const selectedCategory = vars && vars.selectedCategory ? String(vars.selectedCategory) : null;
    sourceRows = selectedCategory
      ? node.googleMapExcelData.filter(row => {
          const cat = row.category;
          if (!cat) return false;
          // category can be a JSON array string like '["Heritage","Religious"]' or a plain string
          try {
            const parsed = typeof cat === "string" && cat.startsWith("[") ? JSON.parse(cat) : null;
            if (Array.isArray(parsed)) return parsed.includes(selectedCategory);
          } catch (_) {}
          if (Array.isArray(cat)) return cat.includes(selectedCategory);
          return String(cat) === selectedCategory;
        })
      : node.googleMapExcelData;
  }

  sourceRows=applyListFunctions(sourceRows,node.googleMapListFn,vars);
  let markers=(sourceRows||[])
    .filter(row=>{
      const latRaw=(row?.[latCol]??row?.lat);
      const lngRaw=(row?.[lngCol]??row?.lng);
      return latRaw!=null&&lngRaw!=null;
    })
    .map((row,idx)=>({
      lat:parseFloat(row?.[latCol]??row?.lat),
      lng:parseFloat(row?.[lngCol]??row?.lng),
      title:resolveDataValue(titleCol,row)??row?.title??`Location ${idx+1}`,
      data:row?.data??row,
    }));

  const centerLocation = resolvedCenter ||
    (node.googleMapCenterLat && node.googleMapCenterLng ? {
      lat: parseFloat(node.googleMapCenterLat),
      lng: parseFloat(node.googleMapCenterLng),
      title: node.googleMapCenterTitle || "Center Location",
    } : null);

  // Use resolved/explicit center or fall back to average of all markers so the map
  // opens on the data rather than the New York default
  const computedStart = (() => {
    if (centerLocation) return { lat: centerLocation.lat, lng: centerLocation.lng };
    if (markers.length > 0) {
      const avgLat = markers.reduce((s, m) => s + m.lat, 0) / markers.length;
      const avgLng = markers.reduce((s, m) => s + m.lng, 0) / markers.length;
      return { lat: avgLat, lng: avgLng };
    }
    return { lat: node.googleMapStartingLat || 40.7128, lng: node.googleMapStartingLng || -74.006 };
  })();

  const infoCardGroupId=node.googleMapInfoCardGroupId;
  let infoCardGroup=null;
  if(infoCardGroupId&&screenNodes){
    const findGroupById=(nodes,id)=>{
      for(const n of nodes||[]){
        if(n.id===id&&n.type==="group")return n;
        if(n.children){const found=findGroupById(n.children,id);if(found)return found;}
      }
      return null;
    };
    infoCardGroup=findGroupById(screenNodes,infoCardGroupId);
  }

  const renderGroup=(group,itemData)=>{
    const gs=group.styles;
    return(
      <div style={{
        position:"relative",
        width:gs.width,
        height:gs.height,
        overflow:"hidden",
        ...bgStyleObj(gs, itemData),
        borderRadius: gs.borderRadius || 0,
        border: gs.borderWidth ? `${gs.borderWidth}px solid ${gs.borderColor || "#36454F"}` : "none",
        boxShadow: shadowCSS(gs),
        filter: gs.blur ? `blur(${gs.blur}px)` : undefined,
        opacity: (gs.opacity ?? 100) / 100,
        animation: animationCSS(gs),
      }}>
        {group.children.map(child=>{
          if(child.type==="group")
            return<GroupRender key={child.id} node={child} dataCtx={itemData} vars={vars} onVarChange={onVarChange} onAction={onAction} apiStore={{}} rssStore={{}} globalDataStore={globalDataStore} screenNodes={screenNodes} screenRef={screenRef}/>;
            return<LeafRender key={child.id} node={child} dataCtx={itemData} vars={vars} onVarChange={onVarChange} onAction={onAction} screenNodes={screenNodes} screenRef={screenRef} globalDataStore={globalDataStore}/>;
        })}
      </div>
    );
  };

  const mapOptions={
    showRoadNames:node.googleMapShowRoadNames!==false,
    showRivers:node.googleMapShowRivers!==false,
    showWaterBodies:node.googleMapShowWaterBodies!==false,
    showPoi:node.googleMapShowPoi!==false,
    showTransit:node.googleMapShowTransit!==false,
    showScale:node.googleMapShowScale||false,
    mapType:node.googleMapMapType||"roadmap",
    mapTheme:node.googleMapMapTheme||"default",
    customMapStyles:node.googleMapCustomMapStyles||"",
  };

  return(
    <div style={{...base,overflow:"hidden", cursor: hasNodeActions ? "pointer" : "default"}} onClick={hasNodeActions ? runNodeActions : undefined}>
      <GoogleMapWidget
        apiKey={node.googleMapApiKey||""}
        startingLat={computedStart.lat}
        startingLng={computedStart.lng}
        zoom={node.googleMapZoom||12}
        markers={markers}
        selectedMarkerIndex={selectedMarker}
        onMarkerSelect={setSelectedMarker}
        showInfoCard={node.googleMapShowInfoCard!==false}
        centerLocation={centerLocation}
        showRoutes={node.googleMapShowRoutes||false}
        mapOptions={mapOptions}
        infoCardGroupId={infoCardGroupId}
        infoCardGroup={infoCardGroup}
        renderGroup={infoCardGroup?renderGroup:null}
        markerColor={node.googleMapMarkerColor||"gray"}
        selectedMarkerColor={node.googleMapSelectedMarkerColor||"red"}
        markerType={node.googleMapMarkerType||"circle"}
        routeColor={node.googleMapRouteColor||"#6366f1"}
        routeWeight={node.googleMapRouteWeight||4}
        routeOpacity={node.googleMapRouteOpacity||0.8}
        centerLabelText={node.googleMapCenterLabelText||""}
        centerLabelBg={node.googleMapCenterLabelBg||"#1a2228"}
        centerLabelColor={node.googleMapCenterLabelColor||"#ffffff"}
        centerLabelBorderRadius={node.googleMapCenterLabelBorderRadius||20}
        centerLabelFontSize={node.googleMapCenterLabelFontSize||12}
        centerLabelBorderColor={node.googleMapCenterLabelBorderColor||"rgba(255,255,255,0.2)"}
        showMarkerLabel={node.googleMapShowMarkerLabel||false}
        markerLabelBg={node.googleMapMarkerLabelBg||"rgba(15,22,28,0.88)"}
        markerLabelColor={node.googleMapMarkerLabelColor||"#ffffff"}
        markerLabelBorderRadius={node.googleMapMarkerLabelBorderRadius||10}
        markerLabelFontSize={node.googleMapMarkerLabelFontSize||10}
      />
    </div>
  );
}



  if (node.type === "video") {
    const videoSrc = (node.dynamic && txt) || node.staticText || "";
    return (
      <div style={{ ...base, cursor: hasNodeActions ? "pointer" : "default" }} onClick={hasNodeActions ? runNodeActions : undefined}>
        {videoSrc ? (
          <video
            src={videoSrc}
            poster={node.videoPoster || undefined}
            autoPlay={node.videoAutoPlay || false}
            controls={node.videoControls !== false}
            loop={node.videoLoop || false}
            muted={node.videoMuted || false}
            playsInline={node.videoPlaysinline !== false}
            onEnded={hasEndActions ? runOnEnd : undefined}
            style={{ width: "100%", height: "100%", objectFit: s.objectFit || "cover", display: "block", borderRadius: s.borderRadius || 0 }}
          />
        ) : null}
      </div>
    );
  }

  if (node.type === "youtube") {
    const ytSrc = buildYouTubeSrc({...node, staticText:(node.dynamic&&txt)||node.staticText||""}, hasEndActions);
    return (
      <div style={{ ...base, position: "relative", overflow: "hidden" }}>
        {ytSrc ? (
          <>
            <iframe
              src={ytSrc}
              title="YouTube video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              style={{ width: "100%", height: "100%", display: "block", border: "none", borderRadius: s.borderRadius || 0 }}
            />
            {/* Covers the title / channel chip YouTube injects at the top */}
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 52, pointerEvents: "none", background: s.backgroundColor || "#000", borderRadius: `${s.borderRadius || 0}px ${s.borderRadius || 0}px 0 0` }} />
          </>
        ) : null}
      </div>
    );
  }

  if (node.type === "quiz") {
    const sourceNode = node.quizSourceId ? (screenNodes || []).find(n => n.id === node.quizSourceId) : null;
    const storeKey = sourceNode?.storeAs || (node.quizSourceId ? (node.quizSourceId) : "");
    const dynamicQ = storeKey ? globalDataStore?.[storeKey] : null;
    const allQ = (Array.isArray(dynamicQ) ? dynamicQ : null) || node.quizQuestions || [];
    const filtered = node.quizCategory ? allQ.filter(q => !q.category || q.category === node.quizCategory) : allQ;
    const perPage = node.quizQuestionsPerPage || 1;
    const currentPage = typeof vars?.[`${node.id}_page`] === "number" ? vars[`${node.id}_page`] : 0;
    const rawAnswers = vars?.[`${node.id}_answers`];
    const answers = rawAnswers && typeof rawAnswers === "object" ? rawAnswers : {};
    const submitted = Boolean(vars?.[`${node.id}_submitted`]);
    const pageStart = currentPage * perPage;
    const pageQuestions = filtered.slice(pageStart, pageStart + perPage);
    const maxPage = Math.max(0, Math.ceil(filtered.length / perPage) - 1);
    const selectAnswer = (globalIdx, optIdx) => {
      if (submitted) return;
      onVarChange?.(`${node.id}_answers`, { ...answers, [globalIdx]: optIdx });
      if (node.quizAutoAdvance !== false && currentPage < maxPage) {
        setTimeout(() => onVarChange?.(`${node.id}_page`, currentPage + 1), node.quizAutoAdvanceDelay ?? 350);
      }
    };
    const setInputAnswer = (globalIdx, text) => {
      if (submitted) return;
      onVarChange?.(`${node.id}_answers`, { ...answers, [globalIdx]: text });
    };
    const qOptLayout = node.quizOptionLayout || "column";
    const qOptCols = node.quizOptionColumns || 2;
    const qGap = node.quizOptionGap ?? 6;
    const optContainerStyle = qOptLayout === "grid"
      ? { display: "grid", gridTemplateColumns: `repeat(${qOptCols}, 1fr)`, gap: qGap }
      : qOptLayout === "row"
        ? { display: "flex", flexDirection: "row", flexWrap: "wrap", gap: qGap }
        : { display: "flex", flexDirection: "column", gap: qGap };
    const qFontSize = node.quizQuestionFontSize > 0 ? node.quizQuestionFontSize : (s.fontSize || 14);
    const qFontWeight = node.quizQuestionFontWeight || "600";
    const qFontFamily = node.quizQuestionFontFamily || s.fontFamily || "Inter";
    const qAlign = node.quizQuestionAlign || "left";
    const qMarginBottom = node.quizQuestionMarginBottom ?? 10;
    const optFontSize = node.quizOptionFontSize > 0 ? node.quizOptionFontSize : Math.max(10, (s.fontSize || 14) - 2);
    const optFontFamily = node.quizOptionFontFamily || s.fontFamily || "Inter";
    const optBorderRadius = node.quizOptionBorderRadius ?? 6;
    const optDefaultBg = node.quizOptionBg || "rgba(255,255,255,0.06)";
    const optDefaultBorder = node.quizOptionBorderColor || "rgba(255,255,255,0.12)";
    const optDefaultColor = node.quizOptionColor || s.color || "#ffffff";
    const qPX = node.quizContainerPaddingX ?? 12;
    const qPY = node.quizContainerPaddingY ?? 10;
    const qValignMap = { top: "flex-start", center: "center", bottom: "flex-end" };
    const qValign = qValignMap[node.quizContentValign || "top"] || "flex-start";
    const timerMode = node.quizTimerMode || "per-page";
    const timerLimit = node.quizTimeLimit || 0;
    const timerColor = node.quizTimerColor || "#ef4444";
    const timerBg = node.quizTimerBg || "rgba(239,68,68,0.1)";
    const firstQ = pageQuestions[0];
    const effectiveTimerLimit = timerMode === "total" ? timerLimit : (firstQ?.timeLimit > 0 ? firstQ.timeLimit : timerLimit);
    const fmtTime = (sec) => sec >= 60 ? `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}` : `${sec}s`;
    return (
      <div style={{ ...base, backgroundColor: s.backgroundColor || "#2a363d", borderRadius: s.borderRadius || 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {effectiveTimerLimit > 0 && quizTimeLeft !== null && !submitted && (
          <div style={{ padding: "5px 12px", background: timerBg, borderBottom: `1px solid ${timerColor}44`, display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: timerColor, fontWeight: 700, fontFamily: "monospace", minWidth: 36 }}>⏱ {fmtTime(quizTimeLeft)}</span>
            <div style={{ flex: 1, height: 4, background: `${timerColor}33`, borderRadius: 2 }}>
              <div style={{ height: "100%", background: timerColor, borderRadius: 2, width: `${Math.min(100, (quizTimeLeft / effectiveTimerLimit) * 100)}%`, transition: "width 1s linear" }} />
            </div>
            {timerMode === "total" && <span style={{ fontSize: 8, color: timerColor, opacity: 0.7 }}>total</span>}
          </div>
        )}
        {pageQuestions.length === 0 && <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#6b8a96", fontSize: 11 }}>No questions</div>}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: qValign, overflow: "auto" }}>
        {pageQuestions.map((q, pi) => {
          const globalIdx = pageStart + pi;
          const answerType = q.answerType || "mcq";
          const optionsType = q.optionsType || "text";
          const selectedOpt = answers[globalIdx];
          const isAnswered = selectedOpt !== undefined;
          const isInputCorrect = answerType === "input"
            ? (typeof selectedOpt === "string" && selectedOpt.toLowerCase().trim() === (q.correctText || "").toLowerCase().trim())
            : false;
          return (
            <div key={globalIdx} style={{ padding: `${qPY}px ${qPX}px`, borderBottom: `1px solid ${s.borderColor || "#36454F"}33` }}>
              <div style={{ fontSize: qFontSize, fontWeight: qFontWeight, color: s.color || "#ffffff", fontFamily: qFontFamily, marginBottom: q.questionImage ? 6 : qMarginBottom, lineHeight: 1.4, textAlign: qAlign }}>
                {q.question}
              </div>
              {q.questionImage && (
                <div style={{ marginBottom: qMarginBottom, borderRadius: optBorderRadius, overflow: "hidden" }}>
                  <img src={q.questionImage} alt="" style={{ width: "100%", maxHeight: 180, objectFit: "cover", display: "block" }} />
                </div>
              )}
              {answerType === "mcq" && (
                <div style={optContainerStyle}>
                  {(q.options || []).map((opt, oi) => {
                    const optText = typeof opt === "object" ? (opt.text || "") : opt;
                    const optImage = optionsType === "image" && typeof opt === "object" ? opt.image : null;
                    const isSel = isAnswered && oi === selectedOpt;
                    const isCorrect = oi === q.correct;
                    let bg = optDefaultBg, border = optDefaultBorder, color = optDefaultColor;
                    if (submitted) {
                      if (isCorrect) { bg = "rgba(34,197,94,0.15)"; border = "rgba(34,197,94,0.5)"; color = "#22c55e"; }
                      else if (isSel) { bg = "rgba(239,68,68,0.15)"; border = "rgba(239,68,68,0.5)"; color = "#ef4444"; }
                    } else if (isSel) { bg = "rgba(99,102,241,0.2)"; border = "rgba(99,102,241,0.6)"; color = "#a5b4fc"; }
                    if (optImage) {
                      return (
                        <div key={oi} onClick={() => selectAnswer(globalIdx, oi)}
                          style={{ borderRadius: optBorderRadius, background: bg, border: `2px solid ${border}`, cursor: submitted ? "default" : "pointer", transition: "all 0.15s", userSelect: "none", overflow: "hidden" }}>
                          <img src={optImage} alt={optText} style={{ width: "100%", height: 80, objectFit: "cover", display: "block" }} />
                          <div style={{ padding: "5px 8px", fontSize: optFontSize, color, fontFamily: optFontFamily, textAlign: "center" }}>
                            {submitted && isCorrect && <span style={{ marginRight: 4 }}>✓</span>}
                            {submitted && isSel && !isCorrect && <span style={{ marginRight: 4 }}>✗</span>}
                            {optText}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={oi} onClick={() => selectAnswer(globalIdx, oi)}
                        style={{ padding: "7px 11px", borderRadius: optBorderRadius, background: bg, border: `1px solid ${border}`, fontSize: optFontSize, color, fontFamily: optFontFamily, cursor: submitted ? "default" : "pointer", transition: "all 0.15s", userSelect: "none" }}>
                        {submitted && isCorrect && <span style={{ marginRight: 5 }}>✓</span>}
                        {submitted && isSel && !isCorrect && <span style={{ marginRight: 5 }}>✗</span>}
                        {optText}
                      </div>
                    );
                  })}
                </div>
              )}
              {answerType === "input" && (
                <input
                  value={typeof selectedOpt === "string" ? selectedOpt : ""}
                  onChange={e => setInputAnswer(globalIdx, e.target.value)}
                  disabled={submitted}
                  placeholder="Type your answer…"
                  style={{ width: "100%", background: submitted ? (isInputCorrect ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)") : optDefaultBg, border: `1px solid ${submitted ? (isInputCorrect ? "rgba(34,197,94,0.5)" : "rgba(239,68,68,0.5)") : optDefaultBorder}`, borderRadius: optBorderRadius, color: submitted ? (isInputCorrect ? "#22c55e" : "#ef4444") : optDefaultColor, fontSize: optFontSize, fontFamily: optFontFamily, padding: "8px 11px", boxSizing: "border-box", outline: "none" }}
                />
              )}
              {submitted && node.quizShowFeedback !== false && (
                <div style={{ marginTop: 6, fontSize: 10, color: answerType === "input" ? (isInputCorrect ? "#22c55e" : "#ef4444") : (answers[globalIdx] === q.correct ? "#22c55e" : "#ef4444") }}>
                  {answerType === "input"
                    ? (isInputCorrect ? "✓ Correct!" : `✗ Answer: ${q.correctText || ""}`)
                    : (answers[globalIdx] === q.correct
                        ? "✓ Correct!"
                        : `✗ Correct: ${(() => { const c = q.options?.[q.correct]; return typeof c === "object" ? c.text : (c || ""); })()}`)
                  }
                </div>
              )}
            </div>
          );
        })}
        </div>
        {filtered.length > perPage && (
          <div style={{ padding: "6px 12px", borderTop: `1px solid ${s.borderColor || "#36454F"}33`, fontSize: 9, color: "#6b8a96", textAlign: "center", flexShrink: 0 }}>
            Page {currentPage + 1} of {Math.ceil(filtered.length / perPage)}
          </div>
        )}
      </div>
    );
  }

  if (node.type === "checkbox") {
    // Always persist state in vars using node.id + itemIdx as implicit key so hide/show and
    // repeated group instances don't share state
    const effectiveVar = node.inputVar || `_chk_${node.id}_${itemIdx}`;
    const isChecked = vars?.[effectiveVar] !== undefined
      ? Boolean(vars[effectiveVar])
      : (node.checkboxChecked || false);
    const label = txt;
    const size = node.checkboxSize || 18;
    const accent = node.checkboxAccentColor || "#6366f1";
    const pos = node.checkboxLabelPosition || "right";
    const labelGap = node.checkboxLabelGap ?? 8;
    const handleChange = (e) => {
      const checked = e.target.checked;
      setCheckboxChecked(checked);
      onVarChange?.(effectiveVar, checked);
    };
    return (
      <div style={{ ...base, display: "flex", alignItems: "center", padding: "0 4px" }}>
        <label style={{ display: "flex", alignItems: "center", gap: labelGap, cursor: "pointer", flexDirection: pos === "left" ? "row-reverse" : "row" }}>
          <input type="checkbox" checked={isChecked} onChange={handleChange}
            style={{ width: size, height: size, accentColor: accent, cursor: "pointer", flexShrink: 0 }} />
          {label && <span style={{ fontSize: s.fontSize || 14, fontWeight: s.fontWeight || "400", fontFamily: s.fontFamily || "Inter", color: s.color || "#ffffff" }}>{label}</span>}
        </label>
      </div>
    );
  }

  if (node.type === "divider") {
    const isV = node.dividerOrientation === "vertical";
    const thickness = node.dividerThickness || 2;
    const color = node.dividerColor || "#36454F";
    const dStyle = node.dividerStyle || "solid";
    return (
      <div style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {isV
          ? <div style={{ width: thickness, height: "100%", background: dStyle === "solid" ? color : "transparent", borderLeft: dStyle !== "solid" ? `${thickness}px ${dStyle} ${color}` : "none" }} />
          : <div style={{ width: "100%", height: thickness, background: dStyle === "solid" ? color : "transparent", borderTop: dStyle !== "solid" ? `${thickness}px ${dStyle} ${color}` : "none" }} />
        }
      </div>
    );
  }

  if (node.type === "table") {
    const cols = node.tableColumns || [];
    const rows = node.tableRows || [];
    const showHeader = node.tableShowHeader !== false;
    const headerBg = node.tableHeaderBg || "#36454F";
    const headerColor = node.tableHeaderColor || "#ffffff";
    const rowBg = node.tableRowBg || "#2a363d";
    const altRowBg = node.tableAltRowBg || "#222d33";
    const rowColor = node.tableRowColor || "#f0f4f5";
    const borderColor = node.tableBorderColor || "#36454F";
    const striped = node.tableStriped !== false;
    const fs = s.fontSize || 12;
    return (
      <div style={{ ...base, overflow: "hidden", borderRadius: s.borderRadius || 0 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: fs, fontFamily: s.fontFamily || "Inter", tableLayout: "fixed" }}>
          {showHeader && cols.length > 0 && <thead>
            <tr>{cols.map((c, ci) => <th key={ci} style={{ background: headerBg, color: headerColor, padding: "4px 6px", borderBottom: `1px solid ${borderColor}`, textAlign: "left", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label || c.key}</th>)}</tr>
          </thead>}
          <tbody>{rows.map((r, ri) => (
            <tr key={ri}>{cols.map((c, ci) => (
              <td key={ci} style={{ background: striped && ri % 2 === 1 ? altRowBg : rowBg, color: rowColor, padding: "4px 6px", borderBottom: `1px solid ${borderColor}22`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(r[c.key] ?? "")}</td>
            ))}</tr>
          ))}</tbody>
        </table>
      </div>
    );
  }

  if (node.type === "slider") {
    const val = node.inputVar && vars?.[node.inputVar] !== undefined
      ? Number(vars[node.inputVar])
      : (node.sliderDefaultValue ?? 50);
    const min = node.sliderMin ?? 0;
    const max = node.sliderMax ?? 100;
    const step = node.sliderStep ?? 1;
    const accent = node.sliderAccentColor || "#6366f1";
    const label = node.sliderLabel || "";
    return (
      <div style={{ ...base, display: "flex", flexDirection: "column", justifyContent: "center", gap: 4, padding: "0 6px" }}>
        {label && <div style={{ fontSize: (s.fontSize || 12) - 2, color: s.color || "#ffffff", fontFamily: s.fontFamily || "Inter" }}>{label}</div>}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input type="range" min={min} max={max} step={step} value={val}
            onChange={e => { if (node.inputVar) onVarChange?.(node.inputVar, Number(e.target.value)); }}
            style={{ flex: 1, accentColor: accent, cursor: "pointer" }} />
          {node.sliderShowValue !== false && <span style={{ fontSize: (s.fontSize || 12) - 2, color: s.color || "#f0f4f5", fontFamily: "monospace", minWidth: 28, textAlign: "right" }}>{val}</span>}
        </div>
      </div>
    );
  }

  if (node.type === "confetti") {
    const loop = node.confettiLoop !== false;
    const mode = node.confettiMode || "fall";
    return (
      <div style={{ ...base, overflow: "hidden", position: "relative" }}>
        {confettiParticles.map(p => {
          if (mode === "burst") {
            return <div key={p.id} style={{
              position: "absolute", left: "50%", top: "50%",
              width: p.size, height: p.isCircle ? p.size : p.size * 0.5,
              background: p.color,
              borderRadius: p.isCircle ? "50%" : 2,
              animationName: "confettiBurst",
              animationDuration: `${p.duration}s`,
              animationDelay: `${p.delay}s`,
              animationTimingFunction: "ease-out",
              animationFillMode: "forwards",
              animationIterationCount: loop ? "infinite" : 1,
              "--bx": `${p.bx}px`, "--by": `${p.by}px`, "--br": `${p.br}deg`,
            }} />;
          }
          return <div key={p.id} style={{
            position: "absolute",
            left: `${p.x}%`,
            top: mode === "snow" ? "-5%" : "-8%",
            width: p.size,
            height: p.isCircle ? p.size : p.size * 0.5,
            background: p.color,
            borderRadius: p.isCircle ? "50%" : 2,
            animationName: "confettiFall",
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            animationTimingFunction: mode === "snow" ? "linear" : "ease-in",
            animationFillMode: "forwards",
            animationIterationCount: loop ? "infinite" : 1,
          }} />;
        })}
      </div>
    );
  }

  if (node.type === "model3d") {
    return (
      <div style={{ ...base }}>
        <Model3DViewer
          url={node.model3dUrl || ""}
          bg={node.model3dBg || "dark"}
          bgColor={node.model3dBgColor || "#1a2228"}
          autoRotate={node.model3dAutoRotate !== false}
          cameraControls={node.model3dCameraControls !== false}
          exposure={node.model3dExposure || 1}
          shadowIntensity={node.model3dShadowIntensity || 1}
          borderRadius={s.borderRadius || 0}
        />
      </div>
    );
  }

  if (node.type === "html") {
    const varBridge = node.htmlVarBridge !== false;
    const bridgeScript = varBridge
      ? `<script>(function(){window.kioskVars=${JSON.stringify(vars||{})};window.addEventListener('message',function(e){if(e.data&&e.data.type==='elm_vars'){window.kioskVars=e.data.vars;if(typeof window.onKioskVarsUpdate==='function')window.onKioskVarsUpdate(e.data.vars);}});window.setKioskVar=function(name,value){window.parent.postMessage({type:'elm_var_set',name:name,value:value},'*');};})();<\/script>`
      : "";
    // When dynamic mode is on, use the resolved variable value as the HTML source
    const rawHtml = (node.dynamic && txt) ? txt : (node.htmlCode || "");
    // Inject the bridge script right after <body> so it runs before user code
    let srcdoc = rawHtml;
    if (varBridge && srcdoc) {
      srcdoc = srcdoc.replace(/<body([^>]*)>/i, (m) => m + bridgeScript);
      if (!srcdoc.includes(bridgeScript)) srcdoc = bridgeScript + srcdoc;
    }
    return (
      <div style={{ ...base, overflow: "hidden" }}>
        <iframe
          ref={htmlIframeRef}
          srcDoc={srcdoc}
          title="html-embed"
          scrolling={node.htmlScrolling ? "yes" : "no"}
          sandbox="allow-scripts allow-forms allow-pointer-lock allow-popups allow-same-origin"
          style={{ width: "100%", height: "100%", border: "none", display: "block", borderRadius: s.borderRadius || 0 }}
        />
      </div>
    );
  }

  if (node.type === "keyboard") {
    return (
      <div style={{ ...base, overflow: "visible", boxShadow: undefined, pointerEvents: "none" }}>
        <div style={{ pointerEvents: "auto", width: "fit-content" }}>
          <KeyboardElement node={node} onEnter={hasNodeActions ? runNodeActions : undefined} />
        </div>
      </div>
    );
  }

  if (CATALOG_TYPES.includes(node.type)) {
    return (
      <div style={{ ...base, ...bgStyleObj(s, dataCtx) }}>
        <CatalogLeaf node={node} vars={vars} onVarChange={onVarChange} onAction={onAction} globalDataStore={globalDataStore} />
      </div>
    );
  }

  return <div style={{ ...base, background: "#2a363d", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b8a96", fontSize: 10 }}>{node.type}</div>;
}